import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { Skeleton } from '@crehana/compass.ui/skeleton';
import { Paginator, TextsDropdown } from '@crehana/compass.ui/v2';
import { boldFormatter } from '@crehana/i18n';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';
import { Container } from '@crehana/ui';

import { Diplomas, type DiplomasProps } from '@/shared/components';
import { GetListCertificatesCollaboratorQueryVariables } from '@/shared/components/Diplomas/graphql/types/GetListCertificatesCollaboratorQuery';
import { useGetListCertificatesCollaborator } from '@/shared/components/Diplomas/hooks/use-get-list-certificates-collaborator';
import { CertificateTypeFilterEnum } from '@/shared/types/graphql/globals.v5';
import Breadcrumb from '@/views/LearnDashboard/components/Breadcrumb';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';

import { LEARN_DASHBOARD_PAGE_NAME } from '../../../constants';
import { useCrehanaTranslation } from '../../../hooks/useCrehanaTranslation';
import {
  ENROLLMENTS_DEGREES_ROUTE,
  PROJECTS_ROUTE,
} from '../../../routes/common.routes';
import { DIPLOMA_FILTER_COLLABORATOR } from './constants';
import { TFilterDiplomasCollaboratorOption } from './type';

const DiplomasPageNew: React.FC<DiplomasProps> = () => {
  const { logEvent } = useAmplitude();
  const { me } = useMe();
  const { t } = useCrehanaTranslation();
  const { isLoadingPlayroomActivated, isPlayroomEnabled } =
    useLearnDashboardContext();
  const isFromApps = useIsRenderedFromWebView();

  const isB2B = !!me?.isB2B;
  const organizationSlug = me?.organization?.slug;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectAct, setSelectAct] =
    useState<TFilterDiplomasCollaboratorOption>('ALL');

  const {
    listCertificatesCollaborator,
    totalAllCertificates,
    totalCertificatesCourse,
    totalCertificatesTracks,
    totalParticipationCertificates,
    loading: loadingCertificatesCollaborator,
    error: errorCertificatesCollaborator,
    refetch: refetchCertificatesCollaborator,
    totalPages,
  } = useGetListCertificatesCollaborator({
    filterByTypeCertificate: selectAct as CertificateTypeFilterEnum,
    currentPage,
  });

  const optionsFilterCertificates: {
    label: string;
    value: TFilterDiplomasCollaboratorOption;
  }[] = useMemo(
    () => [
      {
        label: t('DOWNLOAD_FILTER_ALL', {
          interpolation: { count: totalAllCertificates || 0 },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.ALL,
      },
      {
        label: t('DOWNLOAD_FILTER_CERTIFICATES_COURSES', {
          interpolation: { count: totalCertificatesCourse || 0 },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.CERTIFICATIONS_COURSE,
      },
      {
        label: t('DOWNLOAD_FILTER_CERTIFICATES_TRACKS', {
          interpolation: { count: totalCertificatesTracks || 0 },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.CERTIFICATIONS_TRACKS,
      },
      {
        label: t('DOWNLOAD_FILTER_PROOF', {
          interpolation: { count: totalParticipationCertificates || 0 },
        }),
        value: DIPLOMA_FILTER_COLLABORATOR.PARTICIPANTS_CERTIFICATIONS,
      },
    ],
    [
      t,
      totalAllCertificates,
      totalCertificatesCourse,
      totalCertificatesTracks,
      totalParticipationCertificates,
    ],
  );

  const onRefetch = useCallback(
    (refetchVariables?: GetListCertificatesCollaboratorQueryVariables) => {
      if (refetchCertificatesCollaborator)
        return refetchCertificatesCollaborator(refetchVariables);
    },
    [refetchCertificatesCollaborator],
  );

  useEffect(() => {
    if (!isLoadingPlayroomActivated) {
      logEvent({
        event: 'Render Home Certificates',
        data: {
          ...getSourceEventParams(),
          ...getCommonAmplitudeProperties(me!, isPlayroomEnabled),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPlayroomActivated, isPlayroomEnabled]);

  return (
    <>
      {!isFromApps && (
        <Breadcrumb
          items={[{ label: t('LABEL_SIDEBAR_ITEM_MY_COURSES_CERTIFICATIONS') }]}
        />
      )}
      <Container className="tw-my-2.4" type="dashboard">
        {loadingCertificatesCollaborator ? (
          <Skeleton className="tw-mb-2 tw-w-[30%] tw-h-2" />
        ) : (
          <h3 className="tw-mb-2.4 tw-font-body3 tw-text-neutral-light-800 dark:tw-text-neutral-light-100">
            {t('DOWNLOAD_DESCRIPTION_GENERAL', {
              formatter: boldFormatter,
              interpolation: {
                diplomasCount: totalAllCertificates,
                certificateCount: totalParticipationCertificates,
              },
            })}
          </h3>
        )}
        <div className="tw-flex tw-flex-row tw-justify-start tw-items-center tw-gap-1">
          <p className="tw-font-subtitle5 tw-font-semibold">
            {t('TYPE_CERTIFICATE')}
          </p>
          <TextsDropdown
            selected={selectAct}
            className="tw-max-w-20 tw-bg-neutral-light-100 dark:tw-bg-transparent"
            hideSearch
            placeholder={t('DOWNLOAD_FILTER')}
            options={optionsFilterCertificates}
            onSelect={e => {
              setCurrentPage(1);
              setSelectAct(e);
            }}
          />
        </div>
      </Container>

      <Diplomas
        amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
        enrollmentsRoutePath={
          isB2B ? `/org/${organizationSlug}/learn/` : '/learn/'
        }
        enrollmentsDegreesRoutePath={ENROLLMENTS_DEGREES_ROUTE.path}
        projectRoutePath={PROJECTS_ROUTE.path}
        loading={loadingCertificatesCollaborator}
        refetch={onRefetch}
        error={errorCertificatesCollaborator}
        actData={listCertificatesCollaborator}
      />
      <div className="tw-flex tw-justify-center tw-items-center tw-my-3.2">
        <Paginator
          variant="numeric"
          totalPages={totalPages || 0}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </>
  );
};

export default DiplomasPageNew;
